import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { generateLayoutBlocks, type GalleryPhoto, type LayoutBlock } from './GalleryGrid';
import CinematicHero from './blocks/CinematicHero';
import RhythmBlock from './blocks/RhythmBlock';
import UniformRow from './blocks/UniformRow';
import PhotoLightbox from './PhotoLightbox';

export interface GalleryViewProps {
   photos: GalleryPhoto[];
   albumId: string;
   onPhotoClick?: (photo: GalleryPhoto, index: number) => void;
   onDownloadPhoto?: (photo: GalleryPhoto) => void;
   onDeletePhoto?: (photo: GalleryPhoto) => void;
   downloadingPhotoIds?: Set<string>;
   canDelete?: boolean;
   currentUser?: { uid: string } | null;
}

export default function GalleryView({
   photos,
   albumId,
   onDownloadPhoto,
   onDeletePhoto,
   downloadingPhotoIds,
   canDelete,
   currentUser,
}: GalleryViewProps) {
   const [lightboxOpen, setLightboxOpen] = useState(false);
   const [lightboxIndex, setLightboxIndex] = useState(0);

   const blocks = useMemo(() => generateLayoutBlocks(photos), [photos]);

   const handlePhotoClick = (index: number) => {
      setLightboxIndex(index);
      setLightboxOpen(true);
   };

   if (photos.length === 0) {
      return (
         <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            No photos to display.
         </Box>
      );
   }

   return (
      <Box sx={{ pb: 4 }}>
         {blocks.map((block: LayoutBlock, blockIndex: number) => {
            if (block.type === 'hero' && block.photos[0]) {
               return (
                  <CinematicHero
                     key={`hero-${blockIndex}-${block.startIndex}`}
                     photo={block.photos[0]}
                     onClick={() => handlePhotoClick(block.startIndex)}
                     currentUser={currentUser}
                     albumId={albumId}
                  />
               );
            }
            if (block.type === 'rhythm') {
               return (
                  <RhythmBlock
                     key={`rhythm-${blockIndex}-${block.startIndex}`}
                     photos={block.photos}
                     startIndex={block.startIndex}
                     onPhotoClick={handlePhotoClick}
                     currentUser={currentUser}
                     albumId={albumId}
                  />
               );
            }
            if (block.type === 'uniform') {
               return (
                  <UniformRow
                     key={`uniform-${blockIndex}-${block.startIndex}`}
                     photos={block.photos}
                     startIndex={block.startIndex}
                     onPhotoClick={handlePhotoClick}
                     currentUser={currentUser}
                     albumId={albumId}
                  />
               );
            }
            return null;
         })}

         {lightboxOpen && (
            <PhotoLightbox
               photos={photos}
               currentIndex={lightboxIndex}
               onClose={() => setLightboxOpen(false)}
               onNavigate={setLightboxIndex}
               onDownload={onDownloadPhoto}
               onDelete={onDeletePhoto}
               downloadingPhotoIds={downloadingPhotoIds}
               canDelete={canDelete}
            />
         )}
      </Box>
   );
}
